"use client"

import { useCallback, useEffect, useState } from "react"
import { usePathname } from "next/navigation"
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react"
import NewsletterForm from "@/components/NewsletterForm"
import { event } from "@/lib/gtag"
import { recordNewsletterStatus } from "@/lib/newsletterAction"
import {
  markNewsletterDismissed,
  markNewsletterSubscribed,
  recordVisit,
  shouldShowNewsletterPrompt,
} from "@/lib/newsletterVisits"

const DELAY_MS = 8000
const CLOSE_AFTER_SUCCESS_MS = 3500

const EXCLUDED_PATHS = [
  "/newsletter",
  "/contact-information",
]

export default function NewsletterPopup() {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    recordVisit()
    if (!shouldShowNewsletterPrompt()) {
      return
    }
    const timer = setTimeout(() => {
      setOpen(true)
      event("newsletter_popup_shown")
    }, DELAY_MS)
    return () => clearTimeout(timer)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
    markNewsletterDismissed()
    void recordNewsletterStatus("dismissed")
    event("newsletter_popup_dismissed")
  }, [])

  const handleSuccess = useCallback(() => {
    markNewsletterSubscribed()
    void recordNewsletterStatus("subscribed")
    event("newsletter_popup_subscribed")
    setTimeout(() => setOpen(false), CLOSE_AFTER_SUCCESS_MS)
  }, [])

  if (isExcluded(pathname)) {
    return null
  }

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-end justify-center overflow-y-auto p-0 sm:items-center sm:p-4">
        <DialogPanel className="relative w-full max-w-lg overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl">
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="absolute right-3 top-3 z-10 flex size-9 items-center justify-center rounded-full bg-black/20 text-white transition-colors hover:bg-black/40"
          >
            <i className="tabler-x-outline size-5"></i>
          </button>

          <div className="bg-gradient-to-br from-[#B8892E] via-[#E6B65C] to-[#FFF2B0] px-6 py-7 text-center sm:px-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4a3610]">
              Auto LifeStyle
            </p>
            <DialogTitle className="mt-2 text-2xl font-bold text-gray-900 sm:text-3xl">
              Get First Look At New Arrivals
            </DialogTitle>
            <p className="mt-2 text-sm font-medium text-[#4a3610]">
              Salvage and rebuildable vehicles sell fast. Be the first to know.
            </p>
          </div>

          <div className="px-6 py-6 sm:px-8">
            <ul className="mb-6 space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <i className="tabler-circle-check-filled mt-0.5 shrink-0 text-amber-500"></i>
                <span>Handpicked vehicles emailed straight to your inbox</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="tabler-circle-check-filled mt-0.5 shrink-0 text-amber-500"></i>
                <span>Price drops and limited-time deals before they go public</span>
              </li>
            </ul>

            <NewsletterForm onSuccess={handleSuccess} />

            <button
              type="button"
              onClick={handleClose}
              className="mt-4 w-full text-center text-xs text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline"
            >
              No thanks, maybe later
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}

function isExcluded(pathname: string) {
  if (EXCLUDED_PATHS.includes(pathname)) {
    return true
  }
  return pathname.endsWith("/buy") || pathname.endsWith("/inquire")
}
